// src/hooks/useModals.js
import { useCallback, useRef, useState } from "react";
import { ROUTE, DEFAULT_TAB, TABS } from "../utils/routes";

const normalizeTab = (kind, tab) =>
  (TABS[kind] || []).includes(tab) ? tab : DEFAULT_TAB[kind];

export function useModals() {
  const [modal, setModal] = useState(null);
  const seqRef = useRef(0);

  const open = useCallback((state) => {
    seqRef.current += 1;
    setModal({ ...state, _seq: seqRef.current });
  }, []);

  const openTask = useCallback(
    (
      taskId = null,
      initialTab = "form",
      parentTaskId = null,
      initialProjectId = null,
      returnToProjectId = null,
      returnToProjectTab = "info",
      returnToTaskId = null,
      returnToTaskTab = "subtasks",
      returnToEmployeeTasksId = null,
      returnToEmployeeTasksRange = null,
      returnToTag = null,
    ) =>
      open({
        type: "task",
        taskId,
        initialTab: normalizeTab(ROUTE.TASK, initialTab),
        parentTaskId,
        initialProjectId,
        returnToProjectId,
        returnToProjectTab,
        returnToTaskId,
        returnToTaskTab,
        returnToEmployeeTasksId,
        returnToEmployeeTasksRange,
        returnToTag,
      }),
    [open],
  );

  const openProject = useCallback(
    (
      projectId = null,
      initialTab = "info",
      returnToProjectId = null,
      returnToProjectTab = "info",
      returnToTaskId = null,
      returnToTaskTab = "form",
      returnToTag = null,
    ) =>
      open({
        type: "project",
        projectId,
        initialTab: normalizeTab(ROUTE.PROJECT, initialTab),
        returnToProjectId,
        returnToProjectTab,
        returnToTaskId,
        returnToTaskTab,
        returnToTag,
      }),
    [open],
  );

  const openTag = useCallback((tag) => open({ type: "tag", tag }), [open]);

  const openDelegationTasks = useCallback(
    (delegationId) => open({ type: "tag", tag: null, delegationId }),
    [open],
  );

  const openAtFile = useCallback(
    (ownerKind, ownerId, fileId) => {
      if (ownerKind === "task") {
        open({
          type: "task",
          taskId: ownerId,
          initialTab: "files",
          highlightFileId: fileId,
        });
      } else {
        open({
          type: "project",
          projectId: ownerId,
          initialTab: "files",
          highlightFileId: fileId,
        });
      }
    },
    [open],
  );

  const openAtFolder = useCallback(
    (ownerKind, ownerId, folderId) => {
      if (ownerKind === "task") {
        open({
          type: "task",
          taskId: ownerId,
          initialTab: "files",
          highlightFolderId: folderId,
        });
      } else {
        open({
          type: "project",
          projectId: ownerId,
          initialTab: "files",
          highlightFolderId: folderId,
        });
      }
    },
    [open],
  );

  const openProjectAccess = useCallback(
    (projectId) => open({ type: "projectAccess", projectId }),
    [open],
  );

  const openChangeReq = useCallback(
    (changeKind, targetType, targetId) =>
      open({ type: "changeReq", changeKind, targetType, targetId }),
    [open],
  );

  const openRoles = useCallback(
    (empId) => open({ type: "roles", employeeId: empId }),
    [open],
  );

  const openDepts = useCallback(
    (empId) => open({ type: "depts", employeeId: empId }),
    [open],
  );

  const openVacation = useCallback(
    (vacationId = null, forEmpId = null) =>
      open({ type: "vacation", vacationId, forEmpId }),
    [open],
  );

  const openDelegation = useCallback(
    () => open({ type: "delegation" }),
    [open],
  );

  const openVacNow = useCallback(() => open({ type: "vacnow" }), [open]);

  const openYearCalendar = useCallback(
    () => open({ type: "yearCalendar" }),
    [open],
  );

  const openEmployeeTasks = useCallback(
    (empId, fromIso = null, toIso = null) =>
      open({
        type: "employeeTasks",
        employeeId: empId,
        fromIso,
        toIso,
      }),
    [open],
  );

  const openCopyTask = useCallback(
    (sourceTaskId, options) => {
      const returnToModal = options?.returnToModal === true;
      const returnToTag = options?.returnToTag || null;
      return open({
        type: "task",
        taskId: null,
        copyFromId: sourceTaskId,
        initialTab: "form",
        parentTaskId: null,
        initialProjectId: null,
        returnToProjectId: null,
        returnToProjectTab: "info",
        returnToTaskId: returnToModal ? sourceTaskId : null,
        returnToTaskTab: "form",
        returnToEmployeeTasksId: null,
        returnToEmployeeTasksRange: null,
        returnToTag,
      });
    },
    [open],
  );

  const openCopyProject = useCallback(
    (sourceProjectId, options) => {
      const returnToModal = options?.returnToModal === true;
      const returnToTag = options?.returnToTag || null;
      return open({
        type: "project",
        projectId: null,
        copyFromId: sourceProjectId,
        initialTab: "info",
        returnToProjectId: returnToModal ? sourceProjectId : null,
        returnToProjectTab: "info",
        returnToTag,
      });
    },
    [open],
  );

  const openTemplateFromTask = useCallback(
    (taskId) => open({ type: "templateFromTask", taskId }),
    [open],
  );

  const openTemplateFromProject = useCallback(
    (projectId) => open({ type: "templateFromProject", projectId }),
    [open],
  );

  const setModalTab = useCallback((tab) => {
    setModal((prev) => (prev ? { ...prev, initialTab: tab } : prev));
  }, []);

  const closeModal = useCallback(() => setModal(null), []);

  const openHelp = useCallback(() => open({ type: "help" }), [open]);

  return {
    modal,
    openTask,
    openProject,
    openProjectAccess,
    openAtFile,
    openAtFolder,
    openChangeReq,
    openTag,
    openDelegationTasks,
    openRoles,
    openDepts,
    openVacation,
    openDelegation,
    openVacNow,
    openYearCalendar,
    openEmployeeTasks,
    openCopyTask,
    openCopyProject,
    openTemplateFromTask,
    openTemplateFromProject,
    openHelp,
    closeModal,
    setModalTab,
  };
}
